import React, { useState, useEffect, useMemo } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { useIntl } from 'react-intl';
import {
  RequestQuoteAnalyticsEventParameters,
  requestQuoteAnalyticsEvents,
} from './analytics';
import { SuccessCheck } from './SuccessCheck';
import {
  AssignedRepSelect,
  Button,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  getCreateOrderPayload,
  createOrder,
  Input,
  Text,
  cn,
  SelectValue,
  InfoIcon,
  createContactRepMessage,
  LoadingIcon,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  RepSelectOptions,
  getShouldTriggerProjectConfirmationDialog,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarTitle,
} from '../../../../../../UILibrary';
import { definedTranslation } from '../../i18n';

const MiniSelectionView = ({
  imgSrc,
  category,
  name,
  manufacturer,
}: {
  imgSrc?: string;
  category?: string;
  name?: string;
  manufacturer?: string;
}) => {
  return (
    <div className="flex items-center gap-4 h-[72px]">
      {imgSrc ? (
        <img
          className="min-w-[72px] w-[72px] h-[72px] rounded-md brightness-95"
          src={`${imgSrc}?width=72&height=72`}
          alt="Selected Product"
        />
      ) : (
        <div className="min-w-[72px] w-[72px] h-[72px] rounded-md bg-[#F5F5F7]" />
      )}

      <div className="flex flex-col">
        {category && (
          <Text variant="Caption3" className="uppercase text-[#86868B]">
            {category}
          </Text>
        )}
        {name && <Text variant="Title5">{name}</Text>}
        {manufacturer && <Text variant="Caption2">{manufacturer}</Text>}
      </div>
    </div>
  );
};

export const RequestQuote = () => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const UNIT_OF_MEASURE_VALUE_MAP: Record<string, string> = {
    'Per Unit': intl?.formatMessage(
      definedTranslation.unitOfMeasure.unitsLabel,
    ),
    'Per Linear Foot': intl?.formatMessage(
      definedTranslation.unitOfMeasure.linearFtLabel,
    ),
    'Per Linear Yard': intl?.formatMessage(
      definedTranslation.unitOfMeasure.linearYardsLabel,
    ),
    'Per Square Foot': intl?.formatMessage(
      definedTranslation.unitOfMeasure.sqFtLabel,
    ),
    'Per Gallon': intl?.formatMessage(
      definedTranslation.unitOfMeasure.gallonsLabel,
    ),
    'Per Linear Meter':
      intl?.formatMessage(definedTranslation.unitOfMeasure.linearMetersLabel) ??
      'm',
    'Per Square Meter':
      intl?.formatMessage(definedTranslation.unitOfMeasure.sqMetersLabel) ??
      'm²',
    // EU standardized units of measure (these do not need intl translations)
    'Per Millimeter': 'mm',
    'Per Centimeter': 'cm',
    'Per Meter': 'm',
    'Per Gram': 'g',
    'Per Kilogram': 'kg',
    'Per Milliliter': 'ml',
    'Per Litre': 'L',
  };

  const [isCreateOrderLoading, setIsCreateOrderLoading] = useState(false);
  const { projects, projectsLoading: isLoadingProjects } =
    useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedVariantImg, setSelectedVariantImg] = useState<string | null>(
    null,
  );
  const [assignedRepId, setAssignedRepId] = useState('');
  const [purchasingQuantity, setPurchasingQuantity] = useState('');
  const [reps, setReps] = useState<RepSelectOptions[]>([]);

  const pluralUnitOfMeasure =
    UNIT_OF_MEASURE_VALUE_MAP[
      window?.productAttributeJson?.pricing_unit_of_measure?.value ?? ''
    ];

  const resetState = () => {
    setAssignedRepId('');
    setPurchasingQuantity('');
    setSelectedVariant('');
    setSelectedVariantImg(null);
    setHasSubmittedSuccessfully(false);
  };

  const openRequestQuoteSidebar = () => {
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
    requestQuoteAnalyticsEvents.requestQuoteCtaClicked(); // Drawer being opened is proxy for for request quote CTA click
  };

  const handleOpen = () => {
    const shouldTriggerProjectConfirmationDialog =
      getShouldTriggerProjectConfirmationDialog();

    // TODO: should we validate the project completion here?
    if (shouldTriggerProjectConfirmationDialog) {
      window.dispatchEvent(
        new CustomEvent('open-project-selection-dialog', {
          detail: {
            currentProjectId: currentProject?.externalId,
            analyticsEventLocation: 'Request quote',
            onCurrentProjectSave: () => {
              openRequestQuoteSidebar();
            },
          },
        }),
      );
    } else {
      openRequestQuoteSidebar();
    }
  };

  const handleClose = (dispatchDrawerClosedEvent = true) => {
    resetState();
    document.body.style.overflow = 'auto';
    setIsOpen(false);

    if (dispatchDrawerClosedEvent) {
      requestQuoteAnalyticsEvents.drawerClosed(getAnalyticsEventOptions());
    }
  };

  const handleProjectUpdated = (
    event: CustomEvent<{
      projectId: string;
      projectName: string;
      projectType: string;
      projectLocation: string;
    }>,
  ) => {
    const { projectId } = event.detail;

    if (projectId) {
      setCurrentProjectId(projectId);
    }
  };

  useEffect(() => {
    if (isOpen) {
      makeSeePricingMeaningfulMove();

      // If there is only one configurable variant, we can auto-select it
      if (
        window.productConfigurableJson &&
        Object.keys(window.productConfigurableJson.images)?.length === 1
      ) {
        const firstVariantId = Object.keys(
          window.productConfigurableJson.images,
        )[0];
        setSelectedVariant(firstVariantId);
        setSelectedVariantImg(
          window.productConfigurableJson?.images?.[firstVariantId]?.[0]?.url,
        );
      }

      // If there is an activeChild query param (a variant), initialize the selected variant to it
      const urlParams = new URLSearchParams(window.location.search);
      const activeChild = urlParams.get('activeChild');
      if (
        activeChild &&
        window.productConfigurableJson?.images?.[activeChild]?.[0]?.url
      ) {
        setSelectedVariant(activeChild);
        setSelectedVariantImg(
          window.productConfigurableJson?.images?.[activeChild]?.[0]?.url,
        );
      }
    }
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('open-request-quote-sidebar', handleOpen);

    return () => {
      window.removeEventListener('open-request-quote-sidebar', handleOpen);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('project-updated', handleProjectUpdated);

    return () => {
      window.removeEventListener('project-updated', handleProjectUpdated);
    };
  }, []);

  const onLearnMoreClicked = () => {
    handleClose(false);
    requestQuoteAnalyticsEvents.learnMoreCtaClicked(getAnalyticsEventOptions());
  };

  const makeSeePricingMeaningfulMove = () => {
    const productSku = window.productAttributeJson?.sku?.value;

    if (!currentProject) {
      // Open the create project modal if no project is selected
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: 'Digital sampling',
          },
        }),
      );

      return;
    }

    if (productSku) {
      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject?.externalId,
          successCallback: async (project: ProjectModel) => {
            const orderData = getCreateOrderPayload(
              productSku,
              project,
              'See Pricing',
            );

            createOrder(orderData);
          },
        });

        return;
      }

      const orderData = getCreateOrderPayload(
        productSku,
        currentProject,
        'See Pricing',
      );

      createOrder(orderData);
    }
  };

  const makeRequestQuoteMeaningfulMove = () => {
    const productSku = window.productAttributeJson?.sku?.value;

    const selectedVariantManufacturerSku =
      selectedVariant !== ''
        ? window.productConfigurableJson?.child_list?.[selectedVariant]
            ?.manufacturer_sku?.value
        : window.productAttributeJson?.manufacturer_sku?.value;
    const quoteDetails = `SKU: ${selectedVariantManufacturerSku}; Intended purchasing quantity: ${purchasingQuantity} ${pluralUnitOfMeasure}`;

    if (productSku) {
      if (!currentProject) {
        // Open the create project modal if no project is selected
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: 'Digital sampling',
            },
          }),
        );

        return;
      }

      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject?.externalId,
          successCallback: async (project: ProjectModel) => {
            const orderData = getCreateOrderPayload(
              productSku,
              project,
              'Request quote',
              { quoteDetails },
            );

            setIsCreateOrderLoading(true);

            createOrder(orderData)
              .then(() => {
                requestQuoteAnalyticsEvents.requestQuoteCompleted({
                  ...getAnalyticsEventOptions(),
                });
              })
              .finally(() => {
                setIsCreateOrderLoading(false);
                setHasSubmittedSuccessfully(true);
              });
          },
        });

        return;
      }

      const orderData = getCreateOrderPayload(
        productSku,
        currentProject,
        'Request quote',
        { quoteDetails },
      );

      setIsCreateOrderLoading(true);

      createOrder(orderData)
        .then(() => {
          requestQuoteAnalyticsEvents.requestQuoteCompleted({
            ...getAnalyticsEventOptions(),
          });
        })
        .finally(() => {
          setIsCreateOrderLoading(false);
          setHasSubmittedSuccessfully(true);
        });
    }
  };

  const onSubmitRequestClicked = () => {
    makeRequestQuoteMeaningfulMove();
    const productId =
      selectedVariant !== ''
        ? selectedVariant
        : window.productAttributeJson?.product_id?.value;
    const hasColorway = !!window?.productConfigurableJson?.attributes?.[478];
    const colorway =
      window?.productConfigurableJson?.images?.[selectedVariant]?.[0]
        ?.color_name;

    if (productId && assignedRepId) {
      createContactRepMessage({
        product_id: parseInt(productId),
        project_id: parseInt(currentProject?.externalId?.toString() ?? ''),
        manufacturer: window.productAttributeJson?.manufacturer?.value ?? '',
        rep_id: assignedRepId,
        product_name: window.productAttributeJson?.name?.value ?? '',
        message_source: 'Request quote',
        message_type: 'quote',
        quote: {
          ...(hasColorway && colorway ? { colorway: colorway } : {}),
          purchasing_qty: purchasingQuantity,
          unit_of_measure: pluralUnitOfMeasure,
        },
      });
    }
  };

  const onGoToInboxClicked = () => {
    // Open the inbox
    localStorage.setItem('inboxOpenLastThread', 'true');
    window.location.href = '/inbox';
    requestQuoteAnalyticsEvents.goToInboxClicked(getAnalyticsEventOptions());
    handleClose(false);
  };

  const getAnalyticsEventOptions = (): RequestQuoteAnalyticsEventParameters => {
    return {
      projectId: currentProject?.externalId?.toString() || undefined,
      projectName: currentProject?.title,
      quantity: purchasingQuantity || undefined,
      unitType: pluralUnitOfMeasure || undefined,
    };
  };

  // Submission
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] =
    useState(false);

  const extractLastSegment = (input: string): string => {
    const segments = input.split('.');
    return segments[segments.length - 1];
  };

  const productId = window.productAttributeJson?.product_id?.value ?? '';
  const initialImage =
    window.productConfigurableJson?.images?.[productId]?.[0]?.url;
  const firstGalleryImage = window.galleryImages?.[0]?.url;

  const mappedProjects = Object.values(projects).map((val) => {
    return {
      label: val.title,
      value: val.externalId.toString(),
    };
  });

  const successContent = (
    <div className="pt-6 flex flex-col gap-8">
      <div className="py-4 border-t border-b border-[#F2F2F2]">
        <MiniSelectionView
          category={
            window.productAttributeJson?.taxonomy_us?.value
              ? extractLastSegment(
                  window.productAttributeJson?.taxonomy_us?.value,
                )
              : undefined
          }
          name={window.productAttributeJson?.name?.value}
          manufacturer={window.productAttributeJson?.manufacturer?.value}
          imgSrc={
            window.productConfigurableJson === undefined
              ? firstGalleryImage
              : selectedVariantImg ?? initialImage ?? firstGalleryImage
          }
        />
      </div>

      <div className="flex flex-col items-center gap-3">
        <SuccessCheck />
        <Text variant="H1" className="pt-1">
          {intl?.formatMessage(definedTranslation.requestQuote.successLabel)}
        </Text>
        <Text>
          {intl?.formatMessage(
            definedTranslation.requestQuote.successSubtextLabel,
          )}
        </Text>
      </div>

      <button
        onClick={onGoToInboxClicked}
        className="w-full rounded-full outline-none flex items-center justify-center"
        style={{
          height: 48,
          backgroundColor: '#4D4E58',
          color: '#FFF',
        }}
      >
        {intl?.formatMessage(definedTranslation.requestQuote.goToInboxBtn)}
      </button>
    </div>
  );

  const editableContent = (
    <>
      <div className="flex w-full pb-3 border-b border-b-[#F2F2F2]">
        <AssignedRepSelect
          onAssignedRepChange={(assignedRepId) =>
            setAssignedRepId(assignedRepId)
          }
          onRepsLoaded={(reps) => setReps(reps)}
        />
      </div>

      {window.productAttributeJson?.price_range_verbose?.value && (
        <Text variant="H2">
          {window.productAttributeJson.price_range_verbose.value}
        </Text>
      )}

      <div className="flex flex-col gap-3 py-6 border-t border-b border-[#F2F2F2]">
        <div className="flex items-center gap-2">
          <Text>
            {intl?.formatMessage(
              definedTranslation.requestQuote.confirmProjectDetailsLabel,
            )}
          </Text>
          {window.productAttributeJson?.brand_price_statement?.value && (
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon />
              </TooltipTrigger>
              <TooltipContent>
                {window.productAttributeJson.brand_price_statement.value}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Select
          value={currentProject?.externalId?.toString() ?? ''}
          onChange={(value, label) => {
            requestQuoteAnalyticsEvents.projectUpdated({
              ...getAnalyticsEventOptions(),
              projectId: value,
              projectName: label ?? '',
            });
            setCurrentProjectId(value);
          }}
          direction="bottom-start"
          options={mappedProjects}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={intl?.formatMessage(
                definedTranslation.requestQuote.selectProjectLabel,
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {mappedProjects.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Input
            min={0}
            type="number"
            placeholder={intl?.formatMessage(
              definedTranslation.requestQuote.intendedPurchasingQuantityLabel,
            )}
            value={purchasingQuantity}
            onChange={(e) => {
              setPurchasingQuantity(e.target.value);
            }}
            onBlur={() => {
              requestQuoteAnalyticsEvents.purchaseQuantityUpdated({
                ...getAnalyticsEventOptions(),
                quantity: purchasingQuantity,
                unitType: pluralUnitOfMeasure,
              });
            }}
          />

          <div className="flex bg-[#F5F5F6] rounded-full h-full items-center pr-5 absolute top-0 right-0">
            {pluralUnitOfMeasure}
          </div>
        </div>
      </div>

      <MiniSelectionView
        category={
          window.productAttributeJson?.taxonomy_us?.value
            ? extractLastSegment(
                window.productAttributeJson?.taxonomy_us?.value,
              )
            : undefined
        }
        name={window.productAttributeJson?.name?.value}
        manufacturer={window.productAttributeJson?.manufacturer?.value}
        imgSrc={
          window.productConfigurableJson === undefined
            ? firstGalleryImage
            : selectedVariantImg ?? initialImage
        }
      />

      {Object.keys(window.productConfigurableJson?.images ?? {}).length > 0 && (
        <div className="flex flex-col gap-3">
          <Text variant="H3">
            {intl?.formatMessage(
              definedTranslation.requestQuote.chooseVariantLabel,
            )}
          </Text>
          <div className="grid grid-cols-3 gap-3">
            {Object.keys(window.productConfigurableJson?.images ?? {}).map(
              (id) => {
                const item = window.productConfigurableJson?.images?.[id]?.[0];
                const isSelected = id === selectedVariant;
                return (
                  <div
                    className="cursor-pointer w-fit relative flex flex-col gap-2 rounded-lg"
                    onClick={() => {
                      setSelectedVariant(id);
                      setSelectedVariantImg(item?.url ?? '');
                    }}
                    key={id}
                  >
                    <div
                      className={cn(
                        'brightness-95 max-w-full p-[2px] rounded-xl border-2 border-transparent',
                        {
                          'border-[#4D4E58]': isSelected,
                        },
                      )}
                    >
                      <img
                        src={`${item?.url}?width=140&height=140`}
                        className="rounded-lg"
                        alt="Product"
                      />
                    </div>
                    <Text variant="Caption1" className="text-[#4D4E58]">
                      {item?.color_name}
                    </Text>
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}
    </>
  );

  const isSubmitDisabled =
    purchasingQuantity === '' ||
    parseInt(purchasingQuantity) === 0 ||
    isLoadingProjects ||
    assignedRepId === '' ||
    assignedRepId === undefined ||
    isCreateOrderLoading;

  const errorTooltipContent = useMemo(() => {
    if (purchasingQuantity === '' || parseInt(purchasingQuantity) === 0) {
      return (
        <p>
          {intl?.formatMessage(
            definedTranslation.requestQuote.nonZeroPurchaseQuantityError,
          )}
        </p>
      );
    } else if (assignedRepId === '' || assignedRepId === undefined) {
      return (
        <p>
          {intl?.formatMessage(
            definedTranslation.requestQuote.assignRepresentativeError,
          )}
        </p>
      );
    } else if (reps?.length === 0) {
      return (
        <p>
          {intl?.formatMessage(definedTranslation.requestQuote.noRepsError)}
        </p>
      );
    }
    return <></>;
  }, [purchasingQuantity, assignedRepId, reps?.length, intl]);

  return (
    <Sidebar isOpen={isOpen} handleClose={handleClose}>
      <SidebarTitle>
        {intl?.formatMessage(
          definedTranslation.requestQuote.pricingAndQuoteLabel,
        )}
      </SidebarTitle>
      <SidebarContent>
        {hasSubmittedSuccessfully ? successContent : editableContent}
      </SidebarContent>
      {!hasSubmittedSuccessfully && (
        <SidebarFooter>
          <div className="w-full flex gap-2">
            <Button
              onClick={onLearnMoreClicked}
              className="w-full"
              disabled={isCreateOrderLoading}
            >
              {intl?.formatMessage(
                definedTranslation.requestQuote.learnMoreBtn,
              )}
            </Button>

            <Tooltip>
              <TooltipTrigger
                disableHover={!isSubmitDisabled}
                className="w-full"
              >
                <Button
                  onClick={onSubmitRequestClicked}
                  variant="primary"
                  disabled={isSubmitDisabled}
                  className="w-full"
                >
                  {isCreateOrderLoading ? (
                    <LoadingIcon />
                  ) : (
                    intl?.formatMessage(
                      definedTranslation.requestQuote.submitRequestBtn,
                    )
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{errorTooltipContent}</TooltipContent>
            </Tooltip>
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};
